import {
  Box,
  Heading,
  Text,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  UnorderedList,
  OrderedList,
  ListItem,
} from '@chakra-ui/react'
import { StepContent } from './HelpStepContent'

const H = (file: string) => `/assets/img/help/${file}`

type Props = {
  border: string
  textSecondary: string
}

/** Case Threads — client/requestor → ticket request → email conversation. */
export function CaseThreadsManual({ border, textSecondary }: Props) {
  return (
    <Box>
      <Divider borderColor={border} mb={6} />
      <Heading size="md" mb={2}>
        Case Threads
      </Heading>
      <Text color={textSecondary} mb={4}>
        Open <strong>Case Threads</strong> (
        <Text as="span" fontFamily="mono" fontSize="sm">
          /case-threads
        </Text>
        ). Subtitle on the page:{' '}
        <em>
          Client or requestor, then the ticket request, then the full email conversation. Mail does not create a parent
          ticket or task.
        </em>{' '}
        Use this module to triage helpdesk email by case: pick a client or requestor, open a Ticket Request, read the
        thread, reply, leave an internal note, or clear Unlinked / Needs review queues. Auto-intake creates a{' '}
        <strong>Ticket Request only</strong> — acknowledge and create the parent ticket from{' '}
        <strong>Incoming Request</strong>.
      </Text>

      <Accordion allowMultiple defaultIndex={[0]}>
        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                1. Layout, Clients / Requestors toggle, and header shortcut
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshot={{
                src: H('ct-01-overview.png'),
                alt: 'Case Threads three-column layout',
                caption:
                  'Case Threads: Clients / Requestors toggle, left parties + Unlinked / Needs review, middle ticket requests, right communication thread with Reply / Mark waiting / Open parent.',
              }}
            >
              <Heading as="h4" size="sm">
                Page chrome
              </Heading>
              <UnorderedList spacing={2} pl={1} mb={3}>
                <ListItem>
                  <strong>Case Threads</strong> — page title.
                </ListItem>
                <ListItem>
                  Subtitle reminds you that email lives on a Ticket Request conversation and does{' '}
                  <strong>not</strong> create a parent ticket or task by itself.
                </ListItem>
                <ListItem>
                  Toggle (exact labels): <strong>Clients</strong> · <strong>Requestors</strong> — switches how the left
                  column is grouped. Changing the toggle clears the selected party, queue, request, and email.
                </ListItem>
              </UnorderedList>
              <Heading as="h4" size="sm">
                Three columns (desktop)
              </Heading>
              <UnorderedList spacing={2} pl={1} mb={3}>
                <ListItem>
                  <strong>Left</strong> — Unlinked / Needs review queues, then clients or requestors.
                </ListItem>
                <ListItem>
                  <strong>Middle</strong> — Ticket Requests for the selected party, or queue emails when a queue is
                  active.
                </ListItem>
                <ListItem>
                  <strong>Right</strong> — full conversation for a request, or triage actions for a queue email.
                </ListItem>
              </UnorderedList>
              <Heading as="h4" size="sm">
                Header shortcut
              </Heading>
              <Text fontSize="sm" mb={2}>
                The header mail icon (aria-label <strong>Unread case threads</strong>) navigates to{' '}
                <Text as="span" fontFamily="mono" fontSize="sm">
                  /case-threads
                </Text>
                . The badge count still reflects flat mailbox unread from the inbox API; open Case Threads for
                case-centered work.
              </Text>
              <Text fontSize="sm" color={textSecondary}>
                Lists and the open thread refresh about every 15 seconds, and also when a new inbox notification arrives.
              </Text>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                2. Left column — Unlinked, Needs review, and parties
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshots={[
                {
                  src: H('ct-02-parties-queues.png'),
                  alt: 'Case Threads parties and queues',
                  caption:
                    'Left column: Unlinked and Needs review (orange count badges), then party rows with optional N new unread badge and request count.',
                },
                {
                  src: H('ct-10-requestors.png'),
                  alt: 'Case Threads grouped by Requestors',
                  caption: 'Requestors toggle — left column lists requestor names/emails instead of clients.',
                },
              ]}
            >
              <Heading as="h4" size="sm">
                Queues (exact labels)
              </Heading>
              <UnorderedList spacing={2} pl={1} mb={3}>
                <ListItem>
                  <strong>Unlinked</strong> — emails with no reliable client match (count badge). Opens the middle column
                  as a queue list.
                </ListItem>
                <ListItem>
                  <strong>Needs review</strong> — ambiguous or medium-confidence matches that need a human decision
                  (count badge).
                </ListItem>
              </UnorderedList>
              <Heading as="h4" size="sm">
                Party rows
              </Heading>
              <UnorderedList spacing={2} pl={1} mb={3}>
                <ListItem>
                  <strong>Label</strong> — client name (Clients mode) or requestor name/email (Requestors mode).
                </ListItem>
                <ListItem>
                  Orange badge <strong>N new</strong> — unread messages for that party (when unread_count &gt; 0).
                </ListItem>
                <ListItem>
                  Gray badge with a number — how many Ticket Requests belong to that party.
                </ListItem>
              </UnorderedList>
              <Text fontSize="sm" color={textSecondary}>
                Empty state: <em>No clients or requestors with ticket requests yet.</em> Selecting a party clears any
                active queue. Selecting a queue clears the selected party.
              </Text>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                3. Middle column — Ticket Requests and queue emails
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshot={{
                src: H('ct-03-request-list.png'),
                alt: 'Case Threads ticket request list',
                caption:
                  'Middle column for a client: request number, unread count, subject, status badge, and Needs attention when human review is required.',
              }}
            >
              <Heading as="h4" size="sm">
                Ticket Request row (party selected)
              </Heading>
              <UnorderedList spacing={2} pl={1} mb={3}>
                <ListItem>
                  <strong>Request number</strong> — e.g. REQ-2026-0101; appends <em>· N unread</em> when unread_count &gt;
                  0.
                </ListItem>
                <ListItem>
                  <strong>Subject</strong> — request subject, else preview text, else the fallback label{' '}
                  <em>Ticket request</em>.
                </ListItem>
                <ListItem>
                  <strong>Status badge</strong> — prefers the <strong>parent ticket</strong> status label when a parent
                  exists; otherwise the Ticket Request status / display_status (examples: Draft, New, Pending, In
                  Progress, Resolved, Closed, Cancelled — catalog labels may vary).
                </ListItem>
                <ListItem>
                  Orange badge <strong>Needs attention</strong> — when <code>needs_human_review</code> is true (for
                  example after a customer reply while waiting, or a match that still needs review).
                </ListItem>
              </UnorderedList>
              <Heading as="h4" size="sm">
                Queue email row (Unlinked / Needs review)
              </Heading>
              <UnorderedList spacing={2} pl={1} mb={3}>
                <ListItem>
                  <strong>Subject</strong> — or <em>(No subject)</em>.
                </ListItem>
                <ListItem>
                  <strong>From</strong> — from_name, else from_email.
                </ListItem>
                <ListItem>
                  <strong>Body preview</strong> — short snippet.
                </ListItem>
                <ListItem>
                  Orange <strong>reason</strong> badge — why the email is in the queue (e.g. no client match, ambiguous
                  match).
                </ListItem>
              </UnorderedList>
              <Text fontSize="sm" color={textSecondary}>
                Empty party list: <em>Select a client or requestor to see ticket requests.</em> Empty queue:{' '}
                <em>This queue is empty.</em>
              </Text>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                4. Communication pane — thread, message types, and actions
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshot={{
                src: H('ct-04-thread.png'),
                alt: 'Case Threads communication pane',
                caption:
                  'Right pane for a selected request: request number, status, Reply, Mark waiting, Open parent (or No parent ticket yet), and Customer / Agent / Internal note / System bubbles.',
              }}
            >
              <Text mb={2} fontSize="sm">
                Empty state before a selection: <em>Select a ticket request to read and reply.</em> Opening a queue email
                marks that inbox message as read.
              </Text>
              <Heading as="h4" size="sm">
                Thread header
              </Heading>
              <UnorderedList spacing={2} pl={1} mb={3}>
                <ListItem>
                  <strong>Request number</strong>
                </ListItem>
                <ListItem>
                  <strong>Status badge</strong> — same parent-preferred display status as the middle column.
                </ListItem>
                <ListItem>
                  <strong>Reply</strong> — opens the Reply drawer (next step).
                </ListItem>
                <ListItem>
                  <strong>Mark waiting</strong> — sets the Ticket Request to waiting on the customer (Pending) and adds a
                  system note. Toast: <em>Marked waiting on customer</em>.
                </ListItem>
                <ListItem>
                  <strong>Open parent</strong> — when a parent ticket exists, navigates to{' '}
                  <Text as="span" fontFamily="mono" fontSize="sm">
                    /tickets/&#123;id&#125;/view
                  </Text>
                  . Otherwise shows <em>No parent ticket yet</em>.
                </ListItem>
              </UnorderedList>
              <Heading as="h4" size="sm">
                Message bubbles (direction labels)
              </Heading>
              <UnorderedList spacing={2} pl={1} mb={3}>
                <ListItem>
                  <strong>Customer</strong> (inbound) — from email, subject, HTML body in a sandboxed iframe (or preview
                  text), optional Cc line, attachment badges (<em>filename</em> and size in bytes).
                </ListItem>
                <ListItem>
                  <strong>Agent</strong> (outbound) — agent reply; indented styling.
                </ListItem>
                <ListItem>
                  <strong>Internal note</strong> — yellow background; timeline only, not emailed.
                </ListItem>
                <ListItem>
                  <strong>System</strong> — automated lines (e.g. marked waiting).
                </ListItem>
              </UnorderedList>
              <Text fontSize="sm" color={textSecondary}>
                Missing preview text shows <em>(No preview stored)</em>. Toast after a successful reply:{' '}
                <em>Reply saved on the case</em>. After an internal note: <em>Internal note added</em>.
              </Text>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                5. Reply drawer — fields
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshot={{
                src: H('ct-05-reply-drawer.png'),
                alt: 'Case Threads Reply drawer',
                caption:
                  'Reply drawer: Subject (locked to this request), body, Cc, Reply all (uses saved To and Cc), Reply, Internal note.',
              }}
            >
              <Text mb={2} fontSize="sm">
                Opened from the thread header <strong>Reply</strong> button. Drawer title: <strong>Reply</strong>.
              </Text>
              <UnorderedList spacing={2} pl={1}>
                <ListItem>
                  <strong>Subject (locked to this request)</strong> — read-only. Locked to the request display subject
                  (typically <em>Re: …</em>). You cannot change the subject for an existing case thread.
                </ListItem>
                <ListItem>
                  Message body — placeholder <em>Write a reply or an internal note</em>. Required before either send
                  action.
                </ListItem>
                <ListItem>
                  <strong>Cc</strong> — optional; placeholder <em>name@client.com</em>. Validated for safe recipient
                  format before a reply (not required for Internal note).
                </ListItem>
                <ListItem>
                  Checkbox <strong>Reply all (uses saved To and Cc)</strong> — includes prior conversation participants
                  when sending a reply.
                </ListItem>
                <ListItem>
                  <strong>Reply</strong> — sends an outbound email on the case and closes the drawer.
                </ListItem>
                <ListItem>
                  <strong>Internal note</strong> — saves a private timeline note (not emailed) and closes the drawer.
                </ListItem>
              </UnorderedList>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                6. Queue triage — Create, Attach, Ignore
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshots={[
                {
                  src: H('ct-06-needs-review.png'),
                  alt: 'Needs review queue email',
                  caption:
                    'Needs review: email selected with Suggested REQ-… badge and actions Create Ticket Request, Attach to existing, Ignore.',
                },
                {
                  src: H('ct-07-unlinked.png'),
                  alt: 'Unlinked queue email',
                  caption:
                    'Unlinked: email with reason badge (e.g. No matching client domain) and the same three triage actions.',
                },
                {
                  src: H('ct-08-attach-modal.png'),
                  alt: 'Attach to existing Ticket Request modal',
                  caption:
                    'Attach modal: Ticket request search (request number, subject, or contact), Cancel, Attach.',
                },
                {
                  src: H('ct-09-create-modal.png'),
                  alt: 'Create Ticket Request from email modal',
                  caption:
                    'Create Ticket Request modal: reminder that only a TR is created; Client search by name or code; Cancel / Create Ticket Request.',
                },
              ]}
            >
              <Heading as="h4" size="sm">
                Queue email detail (right pane)
              </Heading>
              <UnorderedList spacing={2} pl={1} mb={3}>
                <ListItem>
                  Subject (or <em>(No subject)</em>), from name/email, body preview.
                </ListItem>
                <ListItem>
                  Orange badge <strong>Suggested &#123;REQ#&#125;</strong> when the matcher proposes a Ticket Request
                  number.
                </ListItem>
                <ListItem>
                  <strong>Create Ticket Request</strong> — opens the create modal.
                </ListItem>
                <ListItem>
                  <strong>Attach to existing</strong> — opens the attach modal.
                </ListItem>
                <ListItem>
                  <strong>Ignore</strong> — drops the email from the queue. Toast: <em>Email ignored</em>.
                </ListItem>
              </UnorderedList>
              <Heading as="h4" size="sm">
                Attach to existing Ticket Request (modal)
              </Heading>
              <UnorderedList spacing={2} pl={1} mb={3}>
                <ListItem>
                  Title: <strong>Attach to existing Ticket Request</strong>.
                </ListItem>
                <ListItem>
                  Field <strong>Ticket request</strong> — search placeholder{' '}
                  <em>Search by request number, subject, or contact</em>. Options show{' '}
                  <em>REQ-… — subject</em> with contact hint. Empty: <em>No ticket requests match that search</em>.
                  Loading: <em>Searching…</em>.
                </ListItem>
                <ListItem>
                  Buttons: <strong>Cancel</strong>, <strong>Attach</strong> (disabled until a request is selected). Toast:{' '}
                  <em>Email attached</em>.
                </ListItem>
              </UnorderedList>
              <Heading as="h4" size="sm">
                Create Ticket Request (modal)
              </Heading>
              <UnorderedList spacing={2} pl={1}>
                <ListItem>
                  Title: <strong>Create Ticket Request</strong>.
                </ListItem>
                <ListItem>
                  Help text: <em>This creates a Ticket Request only. A parent ticket or task is created later, when
                  someone acknowledges the request.</em>
                </ListItem>
                <ListItem>
                  Field <strong>Client</strong> — placeholder <em>Search by client name or code</em>. Empty:{' '}
                  <em>No clients match that search</em>.
                </ListItem>
                <ListItem>
                  Buttons: <strong>Cancel</strong>, <strong>Create Ticket Request</strong> (requires a client). Toast:{' '}
                  <em>Ticket Request created</em>.
                </ListItem>
              </UnorderedList>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                7. How Case Threads fits Incoming Request and Mailbox Inbox
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent border={border} textSecondary={textSecondary}>
              <OrderedList spacing={2} pl={1} mb={3}>
                <ListItem>
                  Helpdesk mail is ingested into the mailbox. Matching rules attach follow-ups to an existing Ticket
                  Request conversation, or place the message in <strong>Unlinked</strong> /{' '}
                  <strong>Needs review</strong>.
                </ListItem>
                <ListItem>
                  Confident new mail can auto-create a <strong>Ticket Request</strong> only — never a parent ticket or
                  task from this screen.
                </ListItem>
                <ListItem>
                  Agents work the conversation here: reply, internal note, mark waiting, attach / create / ignore for
                  queues.
                </ListItem>
                <ListItem>
                  On <strong>Incoming Request</strong>, acknowledge the Ticket Request and optionally create the parent
                  ticket (see Incoming Request section).
                </ListItem>
                <ListItem>
                  <strong>Mailbox Inbox</strong> (
                  <Text as="span" fontFamily="mono" fontSize="sm">
                    /mailbox-inbox
                  </Text>
                  ) remains a flat mailbox reader (Reply / Reply all / Forward). Prefer <strong>Case Threads</strong> for
                  case-centered triage.
                </ListItem>
              </OrderedList>
              <Heading as="h4" size="sm">
                Waiting, closed-case replies, and reopen window
              </Heading>
              <UnorderedList spacing={2} pl={1} mb={3}>
                <ListItem>
                  If the request was <strong>Mark waiting</strong> (Pending) and the customer replies, the request returns
                  to <strong>Needs attention</strong>. Acknowledge SLA due time is not restarted.
                </ListItem>
                <ListItem>
                  If the related Ticket Request is <strong>closed</strong> and the customer emails again within{' '}
                  <strong>14 days</strong>, the reply is attached and the request can reopen to New with Needs attention
                  (SLA acknowledge clock is not restarted from scratch).
                </ListItem>
                <ListItem>
                  If the close is older than 14 days, the email goes to <strong>Needs review</strong>. Attach to reopen
                  the related request, or <strong>Create Ticket Request</strong> for a new linked case — the system does
                  not silently drop the mail.
                </ListItem>
              </UnorderedList>
              <Text fontSize="sm" color={textSecondary}>
                Suggested day-to-day path: Case Threads for email conversation → Incoming Request to acknowledge /
                create parent → Tickets to submit, dispatch, and close.
              </Text>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  )
}
